/**
 * Product Card — declarative sub-element schema for the Visual Builder.
 *
 * This schema powers the ProductCardEditor's WYSIWYG panel. Every field
 * listed here already exists on the consumer `ProductCard.jsx` (web) and
 * is written to the `card` blob of the /ui-customization store — so this
 * work is 100% backward compatible.
 *
 * Each sub-element (image, name, price, mrp, discount_tag, delivery_tag,
 * add_button, card_shell) has:
 *   • `title`            — human label for the accordion header
 *   • `visibility_key`   — optional key on `card` that toggles this element
 *   • `fields`           — ordered list of controls
 *
 * Field shape:
 *   { key, label, type, min?, max?, step?, options?, hint? }
 *
 * Supported types:
 *   - "text"     free text
 *   - "number"   integer / float
 *   - "color"    color picker
 *   - "select"   dropdown (options=[{value,label}])
 *   - "toggle"   boolean switch
 *   - "font"     text (font family)
 *
 * NEVER add pricing, tax, stock, or checkout keys here — this file is
 * strictly UI presentation.
 */

const yesNo = "toggle";
const num = (min, max, step = 1) => ({ type: "number", min, max, step });
const col = { type: "color" };

export const PRODUCT_CARD_SCHEMA = [
  {
    id: "card_shell",
    title: "Card Frame",
    icon: "Square",
    fields: [
      { key: "radius",           label: "Card Radius (px)",  ...num(0, 40) },
      { key: "shadow",           label: "Card Shadow",       type: "select",
        options: [
          { value: "none",   label: "None" },
          { value: "soft",   label: "Soft" },
          { value: "strong", label: "Strong" },
        ]},
      { key: "padding_x",        label: "Padding X (px)",    ...num(0, 24) },
      { key: "padding_y",        label: "Padding Y (px)",    ...num(0, 24) },
    ],
  },

  {
    id: "image",
    title: "Product Image",
    icon: "Image",
    fields: [
      { key: "image_aspect",     label: "Image Aspect",      type: "select",
        options: [
          { value: "1/1",  label: "Square (1:1)" },
          { value: "4/3",  label: "Landscape (4:3)" },
          { value: "3/4",  label: "Portrait (3:4)" },
          { value: "16/9", label: "Wide (16:9)" },
        ]},
      { key: "image_padding",    label: "Image Padding (px)", ...num(0, 24) },
      { key: "image_position",   label: "Image Position",    type: "select",
        options: [
          { value: "center", label: "Center" },
          { value: "top",    label: "Top" },
          { value: "bottom", label: "Bottom" },
          { value: "custom", label: "Custom Offset" },
        ]},
      { key: "image_offset_y",   label: "Custom Offset Y (%)", ...num(0, 100), hint: "Only when Position = Custom" },
    ],
  },

  {
    id: "name",
    title: "Product Name",
    icon: "Type",
    visibility_key: null, // name always visible
    fields: [
      { key: "name_lines",       label: "Max Lines",         ...num(1, 3) },
      { key: "name_size",        label: "Font Size (px)",    ...num(10, 22) },
      { key: "name_weight",      label: "Font Weight",       type: "select",
        options: [
          { value: 400, label: "Regular (400)" },
          { value: 500, label: "Medium (500)" },
          { value: 600, label: "Semibold (600)" },
          { value: 700, label: "Bold (700)" },
        ]},
    ],
  },

  {
    id: "price",
    title: "Selling Price",
    icon: "IndianRupee",
    fields: [
      { key: "currency_symbol",  label: "Currency Symbol",   type: "text" },
      { key: "currency_position",label: "Currency Position", type: "select",
        options: [
          { value: "before", label: "Before ₹99" },
          { value: "after",  label: "After 99₹" },
        ]},
      { key: "price_size",       label: "Font Size (px)",    ...num(10, 28) },
      { key: "price_weight",     label: "Font Weight",       type: "select",
        options: [
          { value: 500, label: "Medium (500)" },
          { value: 600, label: "Semibold (600)" },
          { value: 700, label: "Bold (700)" },
          { value: 800, label: "Extra Bold (800)" },
        ]},
      { key: "price_color",      label: "Price Color",       ...col },
    ],
  },

  {
    id: "mrp",
    title: "MRP (Strike-through)",
    icon: "Tag",
    visibility_key: "show_mrp",
    fields: [
      { key: "show_mrp",         label: "Show MRP",          type: yesNo },
      { key: "show_discount_pct",label: "Show Discount %",   type: yesNo },
      { key: "mrp_size",         label: "Font Size (px)",    ...num(8, 18) },
      { key: "mrp_weight",       label: "Font Weight",       type: "select",
        options: [
          { value: 400, label: "Regular (400)" },
          { value: 500, label: "Medium (500)" },
          { value: 600, label: "Semibold (600)" },
        ]},
      { key: "mrp_color",        label: "MRP Color",         ...col },
      { key: "mrp_strike_width", label: "Strike Width (px)", ...num(1, 4) },
    ],
  },

  {
    id: "discount_tag",
    title: "Discount Badge",
    icon: "BadgePercent",
    visibility_key: "show_discount_badge",
    fields: [
      { key: "show_discount_badge", label: "Show Discount Badge", type: yesNo },
      { key: "badge_position",   label: "Position",          type: "select",
        options: [
          { value: "top-left",     label: "Top-Left" },
          { value: "top-right",    label: "Top-Right" },
          { value: "bottom-left",  label: "Bottom-Left" },
          { value: "bottom-right", label: "Bottom-Right" },
        ]},
      { key: "badge_style",      label: "Style",             type: "select",
        options: [
          { value: "gradient", label: "Gradient" },
          { value: "solid",    label: "Solid" },
          { value: "outline",  label: "Outline" },
        ]},
      { key: "discount_shape",   label: "Shape",             type: "select",
        options: [
          { value: "rounded",   label: "Rounded" },
          { value: "pill",      label: "Pill" },
          { value: "rectangle", label: "Rectangle" },
          { value: "circle",    label: "Circle" },
          { value: "tag",       label: "Price Tag" },
        ]},
      { key: "discount_label",   label: "Label Text",        type: "text", hint: 'e.g. "% OFF" or "OFF"' },
      { key: "discount_bg_color",   label: "Background",     ...col },
      { key: "discount_text_color", label: "Text Color",     ...col },
      { key: "discount_font_size",  label: "Font Size (px)", ...num(8, 18) },
      { key: "discount_font_weight",label: "Font Weight",    type: "select",
        options: [
          { value: 600, label: "Semibold (600)" },
          { value: 700, label: "Bold (700)" },
          { value: 800, label: "Extra Bold (800)" },
        ]},
      { key: "discount_radius",  label: "Radius (px)",       ...num(0, 40) },
      { key: "discount_shadow",  label: "Shadow",            type: "select",
        options: [
          { value: "none",   label: "None" },
          { value: "soft",   label: "Soft" },
          { value: "strong", label: "Strong" },
        ]},
    ],
  },

  {
    id: "delivery_tag",
    title: "Fast-Delivery Tag",
    icon: "Zap",
    visibility_key: "show_delivery_badge",
    fields: [
      { key: "show_delivery_badge",  label: "Show Delivery Badge", type: yesNo },
      { key: "delivery_badge_text",  label: "Badge Text",       type: "text", hint: 'e.g. "10 min" · "15 MINS"' },
      { key: "delivery_badge_position", label: "Position",      type: "select",
        options: [
          { value: "top-left",      label: "Top-Left" },
          { value: "top-right",     label: "Top-Right" },
          { value: "bottom-left",   label: "Bottom-Left" },
          { value: "bottom-right",  label: "Bottom-Right" },
          { value: "inside-image",  label: "Inside Image" },
          { value: "outside-image", label: "Overlap Bottom" },
        ]},
      { key: "delivery_show_icon",   label: "Show Icon",       type: yesNo },
      { key: "delivery_font_size",   label: "Font Size (px)",  ...num(8, 14) },
      { key: "delivery_bg_color",    label: "Background",      ...col },
      { key: "delivery_text_color",  label: "Text Color",      ...col },
      { key: "delivery_radius",      label: "Radius (px)",     ...num(0, 40) },
    ],
  },

  {
    id: "add_button",
    title: "ADD / + / Buy Button",
    icon: "Plus",
    fields: [
      { key: "add_button_variant", label: "Variant",         type: "select",
        options: [
          { value: "text",       label: "ADD (Text)" },
          { value: "plus",       label: "+ (Icon only)" },
          { value: "cart",       label: "Cart Icon" },
          { value: "text_plus",  label: "ADD + (Text + Icon)" },
          { value: "custom",     label: "Custom label" },
        ]},
      { key: "add_button_text",    label: "Label Text",       type: "text", hint: 'e.g. "ADD", "Buy Now"' },
      { key: "add_button_position",label: "Position",         type: "select",
        options: [
          { value: "inline",              label: "Inline (right of price)" },
          { value: "bottom-right",        label: "Bottom-Right" },
          { value: "image-bottom-right",  label: "On Image Bottom-Right" },
        ]},
      { key: "button_width",       label: "Width (px)",       ...num(40, 220) },
      { key: "button_height",      label: "Height (px)",      ...num(24, 56) },
      { key: "button_radius",      label: "Radius (px)",      ...num(0, 40) },
      { key: "add_bg_color",       label: "Background",       ...col },
      { key: "add_border_color",   label: "Border",           ...col },
      { key: "add_text_color",     label: "Text",             ...col },
      { key: "add_hover_color",    label: "Hover",            ...col },
      { key: "add_font_size",      label: "Font Size (px)",   ...num(9, 18) },
      { key: "add_font_weight",    label: "Font Weight",      type: "select",
        options: [
          { value: 500, label: "Medium (500)" },
          { value: 600, label: "Semibold (600)" },
          { value: 700, label: "Bold (700)" },
          { value: 800, label: "Extra Bold (800)" },
        ]},
      { key: "add_shadow",         label: "Shadow",           type: "select",
        options: [
          { value: "none",   label: "None" },
          { value: "soft",   label: "Soft" },
          { value: "strong", label: "Strong" },
          { value: "glow",   label: "Glow" },
        ]},
    ],
  },

  {
    id: "meta",
    title: "Ratings · Weight · Sold",
    icon: "Star",
    fields: [
      { key: "show_weight",      label: "Show Weight",       type: yesNo },
      { key: "show_rating",      label: "Show Rating",       type: yesNo },
      { key: "show_sold_count",  label: "Show Sold Count",   type: yesNo },
    ],
  },
];

/**
 * Defaults for a fresh card — must mirror consumer `ProductCard.jsx`
 * fallbacks so switching to the builder never regresses the UI.
 */
export const PRODUCT_CARD_DEFAULTS = {
  radius: 14, shadow: "soft", padding_x: 8, padding_y: 8,
  image_aspect: "4/3", image_padding: 4, image_position: "center", image_offset_y: 50,
  name_lines: 2, name_size: 13, name_weight: 500,
  currency_symbol: "₹", currency_position: "before",
  price_size: 16, price_weight: 700, price_color: "",
  show_mrp: true, show_discount_pct: true, mrp_size: 12, mrp_weight: 400, mrp_color: "", mrp_strike_width: 1,
  show_discount_badge: true, badge_position: "top-left", badge_style: "gradient",
  discount_shape: "rounded", discount_label: "% OFF",
  discount_bg_color: "", discount_text_color: "", discount_font_size: 11, discount_font_weight: 800,
  discount_radius: 8, discount_shadow: "soft",
  show_delivery_badge: false, delivery_badge_text: "15 MINS", delivery_badge_position: "top-right",
  delivery_show_icon: true, delivery_font_size: 10,
  delivery_bg_color: "", delivery_text_color: "", delivery_radius: 6,
  add_button_variant: "text", add_button_text: "ADD", add_button_position: "inline",
  button_width: 70, button_height: 32, button_radius: 8,
  add_bg_color: "", add_border_color: "", add_text_color: "", add_hover_color: "",
  add_font_size: 12, add_font_weight: 700, add_shadow: "none",
  show_weight: true, show_rating: true, show_sold_count: true,
};

/** Flatten every field key from the schema — used to sanitize incoming patches. */
export function productCardFieldKeys() {
  const out = new Set();
  PRODUCT_CARD_SCHEMA.forEach((sec) => sec.fields.forEach((f) => out.add(f.key)));
  return out;
}
